import * as Dialog from '@radix-ui/react-dialog';
import Card from 'components/card';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Button } from 'components/button';
import { permissionsService } from 'views/services/permissionsService';
import { toast } from 'App';

interface IProps {
  children: React.ReactNode;
  roleId: string;
  roleName: string;
  onSuccess: () => void;
}

export const DeleteRoleConfirmDialog: React.FC<IProps> = ({
  children,
  roleId,
  roleName,
  onSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setIsOpen(isOpen);
    if (isOpen) return;
  };

  const handleDeleteRole = async () => {
    try {
      setIsLoading(true);
      await permissionsService.deleteRole(roleId);

      toast({
        title: 'Success',
        description: 'Role deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
      setIsOpen(false);
    } catch (error: unknown) {
      const errorMsg =
        (error as unknown as { response?: { data?: { message?: string } } })
          ?.response?.data?.message ||
        (error as unknown as { message?: string })?.message ||
        'Something went wrong';
      setErrorMessage(errorMsg);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        setIsError(false);
        setErrorMessage('');
      }, 3000);
    }
  }, [isError]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
        <Dialog.Content
          onInteractOutside={e => {
            if ((e.target as HTMLElement).closest('.chakra-toast')) {
              e.preventDefault();
            }
          }}
          className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
        >
          <Dialog.Title className="sm-max:w-full sm-max:flex-col-reverse flex justify-center items-center relative text-center text-mauve12 mb-10 text-[17px] font-medium">
            Confirmation
          </Dialog.Title>
          <Card extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}>
            <div className="flex flex-col gap-6 py-4">
              <p className="text-center">
                Are you sure you want to delete the role "{roleName}"? This
                action cannot be undone.
              </p>
              <Button
                onClick={handleDeleteRole}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {isLoading ? 'Loading...' : 'Delete Role'}
              </Button>
              {isError && <span className="text-red-600">{errorMessage}</span>}
            </div>
          </Card>
          <Dialog.Close asChild>
            <button
              type="button"
              className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
              aria-label="Close"
            >
              <MdClose />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
